import { unsafeCSS, html, nothing } from 'lit';
import { state, property } from 'lit/decorators.js';

import { beaconSystemBreakpointSmall } from '@trp-beacon-ui-kit/foundation/style-tokens/js/variables';

import { PopBaseComponent } from '../../../../base/pop-base-component';
import {
  ChartDataPoint,
  ShareClassDropdownOption,
} from './product-center-tile.model';
import { Constants } from './product-center-tile.constants';
import { getCurrencySymbol } from '../../../../common/utils/currency';
import { getNumericValueFromPx } from '../../../../common/utils/formatNumber';
import {
  customFormatDate,
  isQuarterEndDate,
} from '../../../../common/utils/formatDate';
import {
  getAPCDateFormat,
  getLatestCumulativeDataPoint,
} from '../../../product-center-table-wrapper/product-center-table-wrapper.utils';
import { Constants as ProductCenterTableWrapperConstants } from '../../../product-center-table-wrapper/product-center-table-wrapper.constants';

import {
  CumulativeDataPoint,
  FundReturnKey,
  type IPCT,
  Product,
  View,
} from '../../../product-center-table-wrapper/product-center-table-wrapper.model';
import { buildShareClassDescriptor } from '../../../../common/utils/buildShareClass';
import { IconSVG } from '../../../../common/utils/icon';
import productCenterStore, {
  zustandStateToComponent,
} from '../../../store/product-center.store';
import {
  type IProductCenterStore,
  type ISelectedProductsTableView,
} from '../../../store/product-center.store.model';

import style from './product-center-tile.scss?inline';
import { getSelectedProductsField } from '../../../store/product-center.store.utils';

/**
 * @tag pop-pc-tile-component
 * @property {ProductCenterTile} [componentObject] Performance Tile content
 * note: `BeaconChartBase.tooltipPointHTML` cannot be called without adding in the entirety
 * of `BeaconChartBase`, which then injects lots of DOM apis into the final build
 * fix: https://rally1.rallydev.com/#/?detail=/userstory/798220893939
 */
export class PopProductCenterTileComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  @state()
  productCenterState: IProductCenterStore = productCenterStore.getState();

  @state()
  ipct: IPCT = {};

  @property()
  productsGroup: Product[] = [];

  @property()
  selectedProduct = '';

  @property()
  fundReturnKey: FundReturnKey = FundReturnKey.SINCE_INCEPTION;

  @property()
  selectedProductsTileView!: ISelectedProductsTableView;

  @property()
  selectedTab!: number;

  /**
   * @internal
   */
  @state()
  shareClassList: ShareClassDropdownOption[] = [];

  /**
   * @internal
   */
  @state()
  selectedShareClass: ShareClassDropdownOption | undefined;

  /**
   * @internal
   */
  @state()
  isPopoverOpen = false;

  /**
   * @internal
   */
  @state()
  showShareClassDropdown = false;

  /**
   * @internal
   */
  @state()
  chartCategories = '';

  /**
   * @internal
   */
  @state()
  chartSeries: ChartDataPoint[] = [];

  /**
   * @internal
   */
  @state()
  isBodyContentVisible = true;

  private chartConfig = Constants.CHART_CONFIG;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(
      Constants.RESIZE_EVENT,
      this.handleResize.bind(this),
    );
    this.requestUpdate();
  }

  private handleResize() {
    this.isBodyContentVisible =
      window.innerWidth > getNumericValueFromPx(beaconSystemBreakpointSmall);
  }

  private buildCategories(): string {
    const cumulativeChartData = this.getDataFromProduct().cumulativeChart?.data;
    const fundReturnData = cumulativeChartData
      ? Object.values(cumulativeChartData)[0]
      : [];

    if (!fundReturnData?.length) {
      return '';
    }

    const period = this.fundReturnKey;

    let filteredData = fundReturnData;

    if (
      period === FundReturnKey.TEN_YEARS ||
      period === FundReturnKey.SINCE_INCEPTION
    ) {
      filteredData = fundReturnData.filter((dataPoint) =>
        dataPoint.asOfDate ? isQuarterEndDate(dataPoint.asOfDate) : false,
      );
    }

    const dates = filteredData
      .map((dataPoint: CumulativeDataPoint) => dataPoint.asOfDate)
      .filter((asOfDate): asOfDate is string => asOfDate !== null)
      .map((asOfDate: string) => new Date(asOfDate));

    const uniqueDates = Array.from(new Set(dates.map((date) => date.getTime())))
      .map((time) => new Date(time))
      .sort((a, b) => a.getTime() - b.getTime());

    let formatOptions;

    if (this.fundReturnKey === FundReturnKey.TEN_YEARS) {
      formatOptions = { year: Constants.FORMAT_OPTIONS.NUMERIC };
    } else {
      formatOptions = {
        month: Constants.FORMAT_OPTIONS.SHORT,
        year: Constants.FORMAT_OPTIONS.TWO_DIGIT,
      };
    }

    if (period === FundReturnKey.TEN_YEARS) {
      this.chartConfig = {
        ...this.chartConfig,
        xAxis: {
          ...this.chartConfig.xAxis,
          tickInterval: Constants.TICK_INTERVAL_FOR_TEN_YEARS,
        },
      };
    }

    return uniqueDates
      .map((date) =>
        customFormatDate(date.toISOString(), formatOptions, this.locale),
      )
      .join(', ');
  }

  private buildSeries(): ChartDataPoint[] {
    const cumulativeChartData = this.getDataFromProduct().cumulativeChart?.data;
    const fundReturnData = cumulativeChartData
      ? Object.values(cumulativeChartData)[0]
      : [];

    if (!fundReturnData?.length) {
      return [];
    }

    const period = this.fundReturnKey;

    const filteredData = this.filterDataByPeriod(fundReturnData, period);

    const shareClassDescriptor = buildShareClassDescriptor(
      this.componentObject?.product.productName.shareClass,
    );
    const benchmarkName =
      this.componentObject?.product.cumulativeChart.benchmarks[0]?.name ?? '';

    return [
      this.createDataPoints(
        filteredData,
        Constants.CUMULATIVE_DATA_FIELDS.FUND_VALUE,
        shareClassDescriptor,
        false,
      ),
      this.createDataPoints(
        filteredData,
        Constants.CUMULATIVE_DATA_FIELDS.BENCHMARK_VALUES,
        benchmarkName,
        true,
      ),
    ];
  }

  private filterDataByPeriod(
    fundReturnData: CumulativeDataPoint[],
    period: FundReturnKey,
  ): CumulativeDataPoint[] {
    if (
      period === FundReturnKey.TEN_YEARS ||
      period === FundReturnKey.SINCE_INCEPTION
    ) {
      return fundReturnData.filter((dataPoint) =>
        dataPoint.asOfDate ? isQuarterEndDate(dataPoint.asOfDate) : false,
      );
    }
    return fundReturnData;
  }

  private createDataPoints(
    filteredData: CumulativeDataPoint[],
    dataKey: keyof CumulativeDataPoint,
    name: string,
    isBenchmark: boolean,
  ): { name: string; data: any[] } {
    const createDataPoint = (
      dataPoint: CumulativeDataPoint,
      index: number,
      array: CumulativeDataPoint[],
    ) => {
      const value = isBenchmark ? dataPoint[dataKey]?.[0] : dataPoint[dataKey];
      const baseDataPoint = {
        y: value,
        asOfDate: dataPoint.asOfDate,
      };

      if (index === 0 || index === array.length - 1) {
        return { ...baseDataPoint, marker: { enabled: true } };
      }

      return baseDataPoint;
    };

    return {
      name,
      data: filteredData.map(createDataPoint),
    };
  }

  private isGroupedFund(): boolean {
    return this.productsGroup.length > 0;
  }

  private buildShareClassList() {
    this.shareClassList = [...[]];
    const createShareClassOption = (
      product: Product,
    ): ShareClassDropdownOption => ({
      value: product.productName.shareClass,
      description: buildShareClassDescriptor(product.productName.shareClass),
      productCode: product.productCode,
      parentProductCode: product.parentProductCode ?? '',
    });

    if (this.isGroupedFund()) {
      const newShareClassList = this.productsGroup.map((product: Product) => {
        const shareClassOption = createShareClassOption(product);
        if (this.selectedProduct === product.productCode) {
          this.selectedShareClass = shareClassOption;
        }
        return shareClassOption;
      });
      this.shareClassList = [...newShareClassList];
    } else {
      const product = this.componentObject?.product;
      if (product) {
        const newShareClassOption = createShareClassOption(product);
        this.shareClassList = [...this.shareClassList, newShareClassOption];
      }
    }
  }

  private handleShareClassClick(
    event: Event,
    shareClassOption: ShareClassDropdownOption,
  ) {
    event.preventDefault();
    const shareClassPopover = this.shadowRoot.querySelector(
      Constants.POPOVER_TAG,
    );

    this.updateSelectedShareClass(shareClassOption);
    this.productCenterState.setSelectedProductFromGroup(
      shareClassOption.parentProductCode ?? '',
      shareClassOption.productCode,
    );
    this.updateChart();

    shareClassPopover?.close?.();
  }

  private updateSelectedShareClass(shareClassOption: ShareClassDropdownOption) {
    this.selectedShareClass = shareClassOption;
    this.selectedProduct = shareClassOption.productCode;
  }

  private updateChart() {
    this.chartCategories = this.buildCategories();
    this.chartSeries = this.buildSeries();
  }

  private handleCollapseContent() {
    this.isBodyContentVisible = !this.isBodyContentVisible;
  }

  private getDataFromProduct(): Product {
    if (this.productsGroup.length > 0) {
      for (const product of this.productsGroup) {
        if (
          this.selectedShareClass &&
          product.productCode === this.selectedShareClass.productCode
        ) {
          return product;
        }
      }
      return this.componentObject.product;
    } else {
      return this.componentObject.product;
    }
  }

  private checkProductCallback(event: Event) {
    if (event && event.target) {
      const target = event.target as HTMLInputElement;
      if (target.id) {
        this.productCenterState.toggleCheckProduct(
          getSelectedProductsField(View.CHART_TILES, this.selectedTab),
          target.id,
          target.checked,
        );
      }
    }
  }

  private showProductIdentifier() {
    let identifierKey = Constants.TRANSLATION_KEYS.APIR;
    this.ipct?.configuration?.productIdentifiers.forEach((identifier) => {
      if (
        ProductCenterTableWrapperConstants.FUND_FIELD[identifier] !== undefined
      ) {
        identifierKey = Constants.TRANSLATION_KEYS[identifier];
      }
    });
    return html`
      ${this.localize(identifierKey)}:
      ${this.getDataFromProduct().productName[identifierKey]}
    `;
  }

  private renderShareClass() {
    if (this.shareClassList.length && this.shareClassList.length > 1) {
      this.selectedShareClass =
        this.selectedShareClass ?? this.shareClassList[0];
      return html`
        <div
          id="${this.getDataFromProduct()
            .productCode}-shareClass-selector-trigger"
          class="b-typography-sans-serif--caption--200 b-font-weight-bold b-flex b-justify-between share-class-selector"
        >
          <div class="selected-share-class">
            ${this.selectedShareClass?.description}
          </div>
          ${IconSVG.chevronDown()}
        </div>
        <beacon-popover
          class="share-class-selector-popover"
          opens-by="#${this.getDataFromProduct()
            .productCode}-shareClass-selector-trigger"
          placement="bottom-start"
        >
          <beacon-menu>
            <ul class="share-class-list">
              ${this.shareClassList.map((option: ShareClassDropdownOption) => {
                return html`
                  <li
                    id="${option.value}"
                    class="b-typography-sans-serif--caption--200 b-font-weight-bold"
                    @click=${(event: Event) =>
                      this.handleShareClassClick(event, option)}
                  >
                    ${option.description}
                  </li>
                `;
              })}
            </ul>
          </beacon-menu>
        </beacon-popover>
      `;
    } else if (this.shareClassList.length) {
      return html`
        <div
          class="b-typography-sans-serif--caption--200 b-font-weight-bold b-flex b-justify-between share-class-selector"
        >
          ${buildShareClassDescriptor(
            this.componentObject?.product.productName.shareClass,
          )}
        </div>
      `;
    } else {
      return nothing;
    }
  }

  private renderTile() {
    let classValue = '';
    if (this.isBodyContentVisible) {
      classValue = Constants.ELEMENT_CLASS.CARET_UP;
    }

    return html`
      <pop-base-tile-component
        layout="standard"
        position="vertical"
        scale="mini"
        class=""
        floating="true"
      >
        <div slot="body">
          <div class="tile-header">
            <p class="header-element b-flex b-justify-between">
              <span
                class="b-typography-sans-serif--caption--100 b-font-weight-bold vehicle-name"
                id="product-desc"
                >${this.getDataFromProduct().productName.vehicleTypeName}</span
              >
              <beacon-control inline>
                <input
                  class="pop-checkbox"
                  type="checkbox"
                  name="fund-${this.getDataFromProduct().productCode}"
                  id="${this.getDataFromProduct().productCode}"
                  .checked=${this.selectedProductsTileView[
                    getSelectedProductsField(View.CHART_TILES, this.selectedTab)
                  ].includes(this.getDataFromProduct().productCode)}
                  @click="${this.checkProductCallback}"
                  aria-labelledby="product-name"
                  aria-describedby="product-desc"
                />
              </beacon-control>
            </p>
            <p class="header-element">
              <span
                class="b-typography-sans-serif--caption--200 b-font-weight-bold fund-name"
                ><a
                  class="fund-name-link"
                  href="${this.getDataFromProduct().productName.fundLink}"
                  target="_blank"
                  id="product-name"
                  >${this.getDataFromProduct().productName.fundName}</a
                ></span
              >
            </p>
            <div class="investment-vehicle b-flex b-justify-between">
              <span class="b-typography-sans-serif--caption--100 currency-apir">
                ${getCurrencySymbol(
                  this.getDataFromProduct().productName.baseCurrencyName,
                )}
                ${this.getDataFromProduct().productName.baseCurrencyName} &#183;
                ${this.showProductIdentifier()}
              </span>
              <div class="share-class-selector">
                ${this.isBodyContentVisible ? this.renderShareClass() : nothing}
              </div>
            </div>
          </div>
          <div
            class="tile-chart ${!this.isBodyContentVisible
              ? Constants.ELEMENT_CLASS.HIDE_CHART
              : Constants.ELEMENT_CLASS.CHART_TOGGLE}"
          >
            <div class="b-flex b-justify-between">
              <span class="b-typography-sans-serif--caption--100 product-dates">
                ${this.localize(Constants.TRANSLATION_KEYS.INCEPTION)}:
                ${getAPCDateFormat(
                  customFormatDate(
                    this.getDataFromProduct().performance?.inceptionDate,
                    ProductCenterTableWrapperConstants.DATE_FORMATS
                      .MEDIUM_DATE_FORMAT,
                    this.locale,
                  ),
                )}
              </span>
              <span class="b-typography-sans-serif--caption--100 product-dates">
                ${this.localize(Constants.TRANSLATION_KEYS.AS_OF)}
                ${getAPCDateFormat(
                  customFormatDate(
                    getLatestCumulativeDataPoint(
                      this.getDataFromProduct().cumulativeChart?.data[
                        this.fundReturnKey
                      ] ?? [],
                      this.fundReturnKey,
                    )?.asOfDate,
                    ProductCenterTableWrapperConstants.DATE_FORMATS
                      .MEDIUM_DATE_FORMAT,
                    this.locale,
                  ),
                )}
              </span>
            </div>
            <pop-product-center-line-chart
              defer-until="idle"
              class="positive-percentage"
              .series="${this.chartSeries}"
              .options="${this.chartConfig}"
              categories="${this.chartCategories}"
              legend-layout=""
              tooltip-variant="multi-line"
              disable-exporting="true"
            >
            </pop-product-center-line-chart>
          </div>
          <div class="toggle-container" @click=${this.handleCollapseContent}>
            <span class="${classValue}"> ${IconSVG.caretDown()} </span>
          </div>
        </div>
      </pop-base-tile-component>
    `;
  }

  renderComponent() {
    this.buildShareClassList();
    if (this.shareClassList.length) {
      this.chartCategories = this.buildCategories();
      this.chartSeries = this.buildSeries();
      return html`
        ${this.componentObject?.product
          ? this.renderTile(this.componentObject?.product)
          : nothing}
      `;
    } else {
      return html``;
    }
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState, component) => {
        const parentProductCode =
          component?.componentObject?.product.parentProductCode ?? '';
        return {
          ipct: productCenterState.ipct,
          fundReturnKey:
            productCenterState.performanceTileView.currentFundReturnKey,
          selectedProductsTileView:
            productCenterState.performanceTileView.selectedProducts,
          productsGroup: productCenterState.getProductsGroup(parentProductCode),
          selectedProduct:
            productCenterState.getSelectedProductFromGroup(parentProductCode),
          selectedTab: productCenterState.performanceTileView.selectedTab,
        };
      },
    },
    PopProductCenterTileComponent,
  ),
);
